/**
 * Normalizes shunt current value by dividing by 1024
 * @param {number} current - Raw shunt current value
 * @returns {number} - Normalized current value
 */
export const normalizeShuntCurrent = (current) => {
    if (typeof current !== 'number' || isNaN(current)) {
        return 0;
    }
    return current / 1024;
};

/**
 * Processes raw shunt data for visualization
 * @param {Array} data - Array of data points with shunt_current and shunt_soc
 * @returns {Array} - Processed data with normalized values
 */
export const processShuntData = (data) => {
    if (!Array.isArray(data)) {
        return [];
    }

    return data.map(point => ({
        timestamp: Number(point.timestamp),
        shunt_current: normalizeShuntCurrent(point.shunt_current),
        shunt_soc: Number(point.shunt_soc) || 0,
        ...(point.hour_timestamp && { hour_timestamp: Number(point.hour_timestamp) }),
        ...(point.data_points && { data_points: Number(point.data_points) })
    }));
};

/**
 * Formats shunt current for display
 * @param {number} current - Normalized shunt current value
 * @returns {string} - Formatted current with 2 decimal places
 */
export const formatShuntCurrent = (current) => {
    const normalizedValue = normalizeShuntCurrent(current);
    return `${normalizedValue.toFixed(2)} A`;
};

/**
 * Groups shunt data by time intervals
 * @param {Array} data - Array of processed shunt data
 * @param {string} interval - Time interval ('hour', 'day', 'week')
 * @returns {Object} - Grouped data with averages
 */
export const groupShuntData = (data, interval) => {
    const grouped = data.reduce((acc, point) => {
        const date = new Date(point.timestamp);
        let key;
        
        switch(interval) {
            case 'hour':
                key = date.setMinutes(0, 0, 0);
                break;
            case 'day':
                key = date.setHours(0, 0, 0, 0);
                break;
            case 'week':
                key = date.setHours(0, 0, 0, 0) - (date.getDay() * 86400000);
                break;
            default:
                key = point.timestamp;
        }

        if (!acc[key]) {
            acc[key] = {
                timestamp: key,
                points: [],
                currentSum: 0,
                socSum: 0,
                count: 0
            };
        }

        acc[key].points.push(point);
        acc[key].currentSum += normalizeShuntCurrent(point.shunt_current);
        acc[key].socSum += point.shunt_soc;
        acc[key].count++;

        return acc;
    }, {});

    return Object.values(grouped).map(group => ({
        timestamp: group.timestamp,
        shunt_current: group.currentSum / group.count,
        shunt_soc: Math.round(group.socSum / group.count)
    }));
};
